import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { exhaustMap, map, Observable } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

// Prompt set related interfaces
export interface PromptBasic {
    promptIdentifier: string;
    name: string;
}

export enum PromptAccessLevel {
    EMAIL_LEVEL = 'email_level',
    SUBSCRIPTION_LEVEL = 'subscription_level',
    PUBLIC = 'public'
}

export enum PromptSetStatus {
    DRAFT = 'draft',
    IN_REVIEW = 'in_review',
    APPROVED = 'approved',
    REJECTED = 'rejected',
    PUBLISHED = 'published'
}

export interface PromptSetCreatorUpdator {
    name: string;
    outputSchema?: Record<string, any>;
}

export interface PromptCreationInput {
    promptSetId: string;
    name: string;
    description?: string;
    promptIdentifier: string;
    context?: string;
    content: PromptContent[];
    examples?: Example[];
    responseFormat?: string;
    vendor?: string;
    aiModelName?: string;
    parameter?: ModelParam;
}

// Update to match the actual payload structure
export interface PromptConfig {
    prompt_metadata_information: PromptMetadataInformation[];
    prompt_configuration: PromptConfiguration[];
}

export interface PromptMetadataInformation {
    prompt_set_id: string;
    prompt_identifier: string;
    name: string;
    description?: string;
    subscription_id: string;
    createdAt?: string;
    lastModifiedAt?: string;
    metadataDict?: Record<string, any>;
}

export interface PromptConfiguration {
    promptIdentifier: string;
    prompt: PromptComponents;
    isPrivate?: boolean;
    validationIdentifier: string;
    createdBy?: string;
    vendor?: string;
    aiModelName?: string;
    parameter?: ModelParam;
}

export interface PromptComponents {
    content: PromptContent[];
    context?: string;
    examples: Example[];
    response_format?: string;
    tools?: ToolConfig[];
    use_json_mode?: boolean;
}

export interface PromptContent {
    prompt_content: string;
    defaultReplacement?: ReplacementDict[];
    removeIfNotSet?: boolean;
}

export interface ToolConfig {
    type?: string;
    function: FunctionConfig;
}

export interface FunctionConfig {
    name: string;
    description: string;
    parameters: FunctionParameter;
}

export interface FunctionParameter {
    type?: string;
    properties: Record<string, PropertyConfig>;
    required: string[];
}

export interface PropertyConfig {
    type?: string;
    description: string;
    items?: ArrayItemConfig;
    enum?: string[];
}

export interface ArrayItemConfig {
    type?: string;
}

export interface ReplacementDict {
    replacementKey: string;
    replacementValue: string | null;
    replacementType?: string;
    pageInformation?: number | null;
}

export interface Example {
    input: string;
    output: string;
}

export interface ModelParam {
    maxOutputTokens?: number;
    temperature?: number;
    topP?: number;
    topK?: number;
    stopSequences?: string[];
    candidateCount?: number;
    reasoningEffort?: string;
}

export interface PromptDetail {
    promptIdentifier: string;
    name: string;
    promptSetId: string;
    subscriptionId: string;
    description?: string;
    createdAt: string;
    lastModifiedAt?: string;
    context?: string;
    content: PromptContent[];
    examples: Example[];
    responseFormat?: string;
    vendor?: string;
    aiModelName?: string;
    parameter?: ModelParam;
}

export interface PromptUpdateInput {
    promptIdentifier: string;
    name?: string;
    description?: string;
    context?: string;
    content?: PromptContent[];
    examples?: Example[];
    responseFormat?: string;
    vendor?: string;
    aiModelName?: string;
    parameter?: ModelParam;
}

export interface PromptSet {
    name: string;
    outputSchema?: Record<string, any>;
    prompt_set_id: string;
    subscription_id?: string;
    created_at?: string;
    prompts?: PromptBasic[];
    accessLevel?: PromptAccessLevel;
    access_level?: string; // Backend returns snake_case
    status?: PromptSetStatus;
    published?: boolean;
    publishedAt?: string | null;
    published_at?: string | null; // Backend returns snake_case
    selected?: boolean;
}

export interface PromptSetID {
    promptSetId: string;
}

export interface PromptSetUpdateInput {
    promptSetId: string;
    promptSetUpdater: PromptSetCreatorUpdator;
}

export interface PromptSetList {
    promptSets: PromptSet[];
}

export interface PromptSetListQuery {
    subscription_id?: string | null;
    access_level?: PromptAccessLevel | null;
    published?: boolean | null;
    status?: PromptSetStatus | null;
}

export interface PublishPromptSetInput {
    promptSetId: string;
    access_level?: PromptAccessLevel;
}

export interface PromptID {
    promptIdentifier: string;
}

export interface BasicResponse {
    message: string;
}

// Add ranking interfaces
export interface PromptRankItem {
    promptIdentifier: string;
    name: string;
    rank: number;
}

export interface SetRankInput {
    prompt_list: PromptRankItem[];
}

// Output schema interfaces
export enum FieldType {
    STR = 'str',
    INT = 'int',
    FLOAT = 'float',
    DICT = 'dict',
    LIST = 'list'
}

export interface DoraFieldConfig {
    field_name: string;  // Pattern: ^[a-z0-9_]+$
    field_type: FieldType;
    default_value?: any;
    expected_values?: any[];
}

export interface OutputSchema {
    fields: DoraFieldConfig[];
}

@Injectable({
    providedIn: 'root'
})
export class PromptSetApiService {

    constructor(private http: HttpClient, private route: ActivatedRoute) { }

    /**
     * Create a new prompt set
     * @param promptSet The prompt set data to create
     */
    createPromptSet(promptSet: PromptSetCreatorUpdator): Observable<PromptSet> {
        const url = `api/v1/flow/execution/create_prompt_set`;
        return this.http.post<PromptSet>(url, promptSet);
    }

    /**
     * Get a list of prompt sets based on query parameters
     * @param query Optional query parameters
     */
    listPromptSets(query?: PromptSetListQuery, shouldIncludeSubscriptionId?: boolean): Observable<PromptSet[]> {
        const url = `api/v1/flow/execution/list_prompt_sets`;
        const baseQuery: PromptSetListQuery = query || { subscription_id: null };

        return this.route.queryParams.pipe(
            map((params: any) => params['subscription_id'] as string | undefined),
            exhaustMap((routeSubscriptionId) => {
                // Build a fresh object each time to avoid mutating the caller's query across emissions.
                const requestBody: PromptSetListQuery = { ...baseQuery };
                if (shouldIncludeSubscriptionId) {
                    // URL query param takes precedence (dashboard navigation); fall back to what the caller supplied.
                    requestBody.subscription_id =
                        routeSubscriptionId ?? baseQuery.subscription_id ?? null;
                }
                return this.http.post<PromptSet[]>(url, requestBody);
            })
        );
    }

    /**
     * Get a specific prompt set by ID
     * @param promptSetId The prompt set ID
     */
    getPromptSet(promptSetId: string): Observable<PromptSet> {
        const url = `api/v1/flow/execution/get_prompt_set`;
        return this.http.post<PromptSet>(url, { promptSetId });
    }

    /**
     * Update an existing prompt set
     * @param updateInput The update data
     */
    updatePromptSet(updateInput: PromptSetUpdateInput): Observable<PromptSet> {
        const url = `api/v1/flow/execution/update_prompt_set`;
        return this.http.post<PromptSet>(url, updateInput);
    }

    /**
     * Delete a prompt set
     * @param promptSetId The prompt set ID to delete
     */
    deletePromptSet(promptSetId: string): Observable<BasicResponse> {
        const url = `api/v1/flow/execution/delete_prompt_set`;
        return this.http.post<BasicResponse>(url, { promptSetId });
    }

    /**
     * Publish a prompt set
     * @param publishInput The publish data
     */
    publishPromptSet(publishInput: PublishPromptSetInput): Observable<BasicResponse> {
        const url = `api/v1/flow/execution/publish_prompt_set`;
        return this.http.post<BasicResponse>(url, publishInput);
    }

    /**
     * Unpublish a prompt set
     * @param promptSetId The prompt set ID to unpublish
     */
    unpublishPromptSet(promptSetId: string): Observable<BasicResponse> {
        const url = `api/v1/flow/execution/unpublish_prompt_set`;
        return this.http.post<BasicResponse>(url, { prompt_set_id: promptSetId });
    }

    /**
     * Store a prompt in a prompt set
     * @param promptConfig The prompt configuration
     */
    storePrompt(promptConfig: PromptConfig): Observable<PromptDetail> {
        const url = `api/v1/flow/execution/store_prompt`;
        return this.http.post<PromptDetail>(url, promptConfig);
    }

    /**
     * Get a prompt by ID
     * @param promptId The prompt ID
     */
    getPrompt(promptId: PromptID): Observable<any> {
        const url = `api/v1/flow/execution/get_prompt`;
        return this.http.post<PromptDetail>(url, promptId);
    }

    /**
     * Update an existing prompt
     * @param updateInput The prompt update data
     */
    updatePrompt(updateInput: PromptUpdateInput): Observable<PromptDetail> {
        const url = `api/v1/flow/execution/update_prompt`;
        return this.http.post<PromptDetail>(url, updateInput);
    }

    /**
     * Delete a prompt
     * @param promptId The prompt ID to delete
     */
    deletePrompt(promptId: PromptID): Observable<BasicResponse> {
        const url = `api/v1/flow/execution/delete_prompt`;
        return this.http.post<BasicResponse>(url, promptId);
    }

    /**
     * Set the ranking order of prompts
     * @param setRankInput The ranking data
     */
    setPromptRanking(setRankInput: SetRankInput): Observable<BasicResponse> {
        const url = `api/v1/flow/execution/set_rank`;
        return this.http.post<BasicResponse>(url, setRankInput);
    }

    /**
     * Update output schema for a prompt set
     * @param promptSetId The prompt set ID
     * @param fields The field configurations
     */
    updateOutputSchema(promptSetId: string, promptSetName: string, fields: DoraFieldConfig[]): Observable<PromptSet> {
        return this.updatePromptSet({
            promptSetId,
            promptSetUpdater: {
                name: promptSetName,
                outputSchema: { fields }
            }
        });
    }
}